"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Droplets, Gauge, Zap, ShieldCheck, Cpu, Layers, ArrowRight, CircleCheck as CheckCircle2, SlidersHorizontal as Sliders, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const technicalBenefits = [{
  id: 1,
  icon: Droplets,
  title: "Atomização ultra-fina sem gotas",
  description: "Tamanhos de gota micrométricos que evaporam rapidamente no ar sem molhar superfícies, produtos ou maquinaria envolvente.",
  tag: "Precisão"
}, {
  id: 2,
  icon: Gauge,
  title: "Controlo rigoroso de humidade (HR)",
  description: "Estabilidade térmica e higrométrica contínua com tolerâncias mínimas, mantendo parâmetros ideais para papel, têxtil, madeira e eletrónica.",
  tag: "Estabilidade"
}, {
  id: 3,
  icon: Zap,
  title: "Supressão de eletricidade estática",
  description: "Eliminação de descargas eletrostáticas perigosas e atritos em processos de alta velocidade, reduzindo perdas e rejeições.",
  tag: "Segurança"
}, {
  id: 4,
  icon: ShieldCheck,
  title: "Fiabilidade e bicos anti-entupimento",
  description: "Mecanismos com agulhas de limpeza automática e materiais de alta resistência ao desgaste químico e mineral.",
  tag: "Durabilidade"
}, {
  id: 5,
  icon: Cpu,
  title: "Integração e automação de caudal",
  description: "Sistemas totalmente compatíveis com controladores PLC/SCADA e tecnologia AutoJet para modulação por pulso (PWM).",
  tag: "Automação"
}, {
  id: 6,
  icon: Layers,
  title: "Eficiência energética e hídrica",
  description: "Minimização do consumo de ar comprimido e água tratada, reduzindo custos operacionais e a pegada ambiental.",
  tag: "Sustentabilidade"
}];
const technicalSpecs = [{
  metric: "< 10 μm",
  label: "Espectro de gota Sauter (D32)",
  detail: "Evaporação homogénea instantânea"
}, {
  metric: "± 2%",
  label: "Tolerância de humidade relativa",
  detail: "Precisão em ambientes controlados"
}, {
  metric: "-45%",
  label: "Consumo de ar comprimido",
  detail: "Com bicos atomizadores otimizados"
}, {
  metric: "99.8%",
  label: "Disponibilidade operacional",
  detail: "Ciclos contínuos de produção 24/7"
}];
export default function Benefits() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="technicalbenefits" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary border-secondary/30 bg-accent">
            <Sparkles className="size-3.5 mr-1" />
            Engenharia e Pulverização
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-5">
            Vantagens técnicas em humidificação industrial
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Controlo rigoroso de humidade relativa, atomização homogénea sem
            molhagem e fiabilidade contínua com tecnologia de ponta Spraying
            Systems Co.
          </p>
        </div>

        {/* Benefits Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {technicalBenefits.map((benefit, i) => {
          const Icon = benefit.icon;
          return <motion.div key={benefit.id} data-index={i} variants={itemVariants} className="h-full">
                <Card className="h-full bg-card border-border shadow-xs hover:border-secondary/50 hover:shadow-md transition-[border-color,box-shadow] duration-300">
                  <CardHeader className="gap-3">
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-lg bg-accent text-secondary">
                        <Icon className="size-6" />
                      </div>
                      <Badge variant="secondary" className="text-xs font-medium">
                        {benefit.tag}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-default font-bold text-card-foreground">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Technical Benchmarks & Highlight Banner */}
        <motion.div initial={{
        opacity: 0,
        y: prefersReducedMotion ? 0 : 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="rounded-2xl bg-card border border-border p-8 lg:p-12 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left side info */}
            <div className="lg:col-span-5 space-y-5">
              <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider text-secondary uppercase">
                <Sliders className="size-4" />
                Performance Certificada
              </div>
              <h3 className="text-2xl sm:text-3xl font-default font-bold text-card-foreground leading-snug">
                Fiabilidade comprovada para processos contínuos
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                Desenvolvemos soluções personalizadas com suporte de
                engenharia local da Eurocontrol desde 1993, assegurando bicos e
                controladores calibrados para a sua aplicação específica.
              </p>
              <div className="pt-2 flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="w-full sm:w-auto">
                  <Link to="/ContactsPage" className="inline-flex items-center gap-2">
                    Consulte-nos
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                  <Link to="/TechnicalSolutionsPage">Soluções técnicas</Link>
                </Button>
              </div>
            </div>

            {/* Right side specs cards */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {technicalSpecs.map((spec, i) => <div key={spec.label} data-index={i} className="p-5 rounded-xl bg-background border border-border flex flex-col justify-between">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-2xl sm:text-3xl font-default font-extrabold text-primary">
                      {spec.metric}
                    </span>
                    <CheckCircle2 className="size-5 text-secondary shrink-0" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-1">
                      {spec.label}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {spec.detail}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}